import { memo, useCallback } from 'react'
import type { ChannelInventory } from '../../lib/adsApi.js'
import {
  attentionCounts,
  hasAttention,
  pressureTone,
  type AttentionKey,
} from '../../lib/adsPacing.js'

/**
 * Tête de la régie publicitaire.
 *
 * Elle ne résume rien : elle n'affiche que ce qui demande une décision, et
 * chaque compte est un filtre de la liste juste en dessous. Quand tout tient,
 * la barre se réduit à une ligne calme et rend sa place au catalogue — un
 * tableau de bord qui occupe la même surface qu'il ait ou non quelque chose à
 * dire est un tableau de bord qu'on cesse de lire.
 */

interface AdsAttentionBarProps {
  inventory:  ChannelInventory | null
  activeFilter: AttentionKey | null
  onFilter:   (key: AttentionKey | null) => void
}

const CHIP_LABELS: Record<AttentionKey, (count: number) => string> = {
  unreachable: count => `${count} intenable${count > 1 ? 's' : ''}`,
  tight:       count => `${count} à marge mince`,
  endedShort:  count => `${count} engagement${count > 1 ? 's' : ''} manqué${count > 1 ? 's' : ''}`,
}

const CHIP_TONES: Record<AttentionKey, string> = {
  unreachable: 'danger',
  tight:       'warning',
  endedShort:  'danger',
}

const CHIP_ORDER: AttentionKey[] = ['unreachable', 'endedShort', 'tight']

export const AdsAttentionBar = memo(function AdsAttentionBar({ inventory, activeFilter, onFilter }: AdsAttentionBarProps) {
  if (!inventory) return null

  const counts = attentionCounts(inventory.campaigns)
  const tone   = pressureTone(inventory.pressurePct)

  return (
    <section className="ads-attention" aria-label="État de la régie publicitaire">
      {hasAttention(counts) ? (
        <div className="ads-attention__chips">
          {CHIP_ORDER.filter(key => counts[key] > 0).map(key => (
            <AttentionChip
              key={key}
              chipKey={key}
              label={CHIP_LABELS[key](counts[key])}
              tone={CHIP_TONES[key]}
              pressed={activeFilter === key}
              onFilter={onFilter}
            />
          ))}
        </div>
      ) : (
        <p className="ads-attention__calm" role="status">
          Toutes les campagnes tiennent leur engagement.
        </p>
      )}

      <div className="ads-attention__pressure">
        <span className="ads-attention__pressure-label">Pression de l’inventaire</span>
        {/* Espace insécable : « 112 % » ne doit pas se couper en fin de ligne. */}
        <strong className={`ads-attention__pressure-value is-${tone}`}>{inventory.pressurePct}&nbsp;%</strong>
        <span className="ads-attention__pressure-detail">
          {inventory.demandSpots} à placer · {inventory.capacitySpots} posables
          {inventory.horizonTruncated ? ' (120 j)' : ''}
        </span>
      </div>
    </section>
  )
})

const AttentionChip = memo(function AttentionChip({ chipKey, label, tone, pressed, onFilter }: {
  chipKey: AttentionKey
  label:   string
  tone:    string
  pressed: boolean
  onFilter: (key: AttentionKey | null) => void
}) {
  const handleClick = useCallback(() => onFilter(pressed ? null : chipKey), [chipKey, pressed, onFilter])

  return (
    <button
      type="button"
      className={`ads-attention__chip is-${tone}${pressed ? ' is-pressed' : ''}`}
      aria-pressed={pressed}
      onClick={handleClick}
    >
      {label}
    </button>
  )
})
